import { inject, Injectable } from '@angular/core';
import { Firestore, doc, getDoc, setDoc, updateDoc, serverTimestamp } from '@angular/fire/firestore';

/**
 * Reads the top-level `events/{eventId}` document (branding, config, dates) that
 * several components need. Shared seam so components don't inject Firestore just
 * to read the event doc. Part of the WebAdmin MVVM migration; see
 * tools/no-firestore-in-components.mjs.
 */
@Injectable({ providedIn: 'root' })
export class EventsRepository {
  private firestore = inject(Firestore);

  /** The event document data, or null if it doesn't exist. */
  async getEvent(eventId: string): Promise<Record<string, unknown> | null> {
    const snap = await getDoc(doc(this.firestore, `events/${eventId}`));
    return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
  }

  /**
   * updateDoc the event doc with the caller's exact payload (no fields added).
   * Use when the caller already includes its own updatedAt; fails if the doc
   * doesn't exist (it always does for an open event).
   */
  patchEvent(eventId: string, patch: Record<string, unknown>): Promise<void> {
    return updateDoc(doc(this.firestore, `events/${eventId}`), patch);
  }

  /** Merge-patch the event doc (stamps updatedAt). */
  updateEvent(eventId: string, patch: Record<string, unknown>): Promise<void> {
    return setDoc(
      doc(this.firestore, `events/${eventId}`),
      { ...patch, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
}
